import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import { Col, Container, Row, Input } from 'reactstrap';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import apiConfigurations from 'api/configurations';
import apiProjects from 'api/projects';
import BasicTable from 'components/layout/tables/BasicTable';
import IConfiguration from 'components/interfaces/IConfiguration';
import IProject from 'components/interfaces/IProject';
import IRouteParams from 'interfaces/IRouteParams';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SearchInput from 'components/layout/forms/SearchInput';
import { addApproval, addMessage } from 'store/messages/actions';
import { convertToReadableDate } from '../configurator/services/formatters/convertToReadableDatets';
import { convertToFullName } from '../configurator/services/formatters/convertToFullName';

import DownloadExcelList from './src/project/DownloadExcelList';
import translations from '../../services/translations/translations';
import { allowedToWriteOwnOrganizationConfiguration } from '../configurator/services/permissions/allowedToWriteOwnOrganizationConfiguration';
import { convertToReadableDecimal } from '../configurator/services/formatters/convertToReadableDecimal';

import 'style/projects/Project.css';

const Project = (): JSX.Element => {
  const { t, i18n } = useTranslation([translations.common]);
  const dispatch = useDispatch();
  const history = useHistory();
  const { projectId } = useParams<IRouteParams>();
  const [project, setProject] = useState<IProject>();
  const [tablePage, setTablePage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [offset, setOffset] = useState(0);
  const [totalSize, setTotalSize] = useState<number>(100);
  const [selectedRow, setSelectedRow] = useState<number>();
  const [searchTerms, setSearchTerms] = useState<string>('');
  const [configurations, setConfigurations] = useState<IConfiguration[]>([]);
  const [allRowsChecked, setAllRowsChecked] = useState(true);
  const currentUser = useSelector((state: any) => state.users.current);

  const configurationRef = useRef(configurations);

  const selectedConfiguration =
    configurations !== undefined && selectedRow !== undefined
      ? configurations[selectedRow]
      : undefined;

  useEffect(() => {
    if (selectedRow !== undefined) {
      history.push(`/configurations/${selectedConfiguration?.id}`);
    }
  }, [history, selectedRow, selectedConfiguration]);

  useEffect(() => {
    const getProjectConfigurations = async () => {
      try {
        const result: IConfiguration[] = await apiConfigurations.getAll(
          projectId,
          {
            limit: pageSize,
            offset,
            search: searchTerms,
          }
        );
        const count = await apiConfigurations.count(projectId, {
          search: searchTerms,
        });

        const resultWithReadableFields = result.map((conf) => ({
          ...conf,
          created: convertToReadableDate(conf.created),
          createdUser: convertToFullName(conf.createdUser),
          updated: convertToReadableDate(conf.updated),
          updatedUser: convertToFullName(conf.updatedUser),
          squareMeters: convertToReadableDecimal(conf.squareMeters),
          isSelected: true,
        }));

        setConfigurations(resultWithReadableFields);
        setTotalSize(count.count);
      } catch (error) {
        dispatch(
          addMessage(
            t(`${translations}:Modal Messages.Get configurations failed`)
          )
        );
      }
    };
    getProjectConfigurations().catch((e) => console.log(e));
  }, [searchTerms, offset, pageSize, i18n.language]);

  useEffect(() => {
    const getProject = async () => {
      try {
        const result = await apiProjects.get(projectId);
        setProject(result);
      } catch (error) {
        dispatch(
          addMessage(t(`${translations}:Modal Messages.Get projects failed`))
        );
      }
    };
    getProject().catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    configurationRef.current = configurations;
  }, [configurations]);

  const goToNewConfiguration = () => {
    history.push(`/projects/${projectId}/configuration`);
  };

  const colTitles = {
    name: t(`${translations.common}:Name`),
    created: t(`${translations.common}:Created`),
    createdUser: t(`${translations.common}:CreatedUser`),
    updated: t(`${translations.common}:Updated`),
    updatedUser: t(`${translations.common}:UpdatedUser`),
    squareMeters: t(`${translations.common}:SquareMeters`),
    excel: t(`${translations.common}:Excel`),
  };

  const onCheckboxClick =
    (id: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const resultWithReadableFields = configurationRef.current.map((config) =>
        config.id === id
          ? {
              ...config,
              isSelected: e.target.checked,
            }
          : config
      );
      setConfigurations(resultWithReadableFields);
    };

  const onAllCheckboxClick = () => {
    const newState = !allRowsChecked;

    const resultWithReadableFields = configurationRef.current.map((config) => {
      return {
        ...config,
        isSelected: newState,
      };
    });
    setConfigurations(resultWithReadableFields);
    setAllRowsChecked(newState);
  };

  const excelFormatter = (id: string): JSX.Element => {
    const configuration = configurations.find((c) => c.id === id);

    if (configuration === undefined) {
      return <></>;
    }

    return (
      <Input
        className="pdf-checkbox"
        checked={configuration.isSelected}
        onChange={onCheckboxClick(id)}
        type="checkbox"
      />
    );
  };

  const columns = [
    {
      dataField: 'name',
      text: colTitles.name,
    },
    {
      dataField: 'created',
      text: colTitles.created,
    },
    {
      dataField: 'createdUser',
      text: colTitles.createdUser,
    },
    {
      dataField: 'updated',
      text: colTitles.updated,
    },
    {
      dataField: 'updatedUser',
      text: colTitles.updatedUser,
    },
    {
      dataField: 'squareMeters',
      text: colTitles.squareMeters,
      align: 'right',
    },
    {
      dataField: 'id',
      formatter: excelFormatter,
      headerStyle: () => ({ width: '3rem' }),
      headerClasses: allRowsChecked ? 'rows-checked' : 'rows-unchecked',
      text: colTitles.excel,
      align: 'center',
      headerEvents: {
        onClick: () => {
          onAllCheckboxClick();
        },
      },
    },
  ];

  const handleTableChange = (type: any, { page, sizePerPage }: any) => {
    setTablePage(page);
    setPageSize(sizePerPage);
    setOffset(page === 0 ? 0 : (page - 1) * sizePerPage);
  };

  const removeProject = async () => {
    await apiProjects.delete(projectId);
    history.push('/projects');
  };

  const onRemoveButtonClick = () => {
    dispatch(
      addApproval({
        callback: removeProject,
        message: t(
          `${translations.common}:Are you sure that you want to remove the project?`
        ),
      })
    );
  };

  const showAddButton =
    currentUser.allowedToWriteConfigurations ||
    allowedToWriteOwnOrganizationConfiguration(currentUser, project);

  return (
    <Container fluid className="project main-container">
      <Row className="row-content title-row">
        <Col>
          <h2>
            <Link to="/projects">{t(`${translations.common}:Projects`)}</Link>
            <span> </span>&gt;<span> </span>
            {project?.name}
          </h2>
          {currentUser.allowedToDeleteConfigurations ? (
            <PrimaryButton onClick={onRemoveButtonClick}>
              {t(`${translations.common}:Remove`)}
            </PrimaryButton>
          ) : null}
        </Col>
      </Row>
      <div className="grey-background">
        <Row>
          <Col>
            <SearchInput setSearchTerms={setSearchTerms} />
          </Col>
          {showAddButton ? (
            <Col>
              <PrimaryButton
                className="move-right"
                onClick={goToNewConfiguration}
              >
                {t(
                  `${translations.common}:Add Configuration`
                ).toLocaleUpperCase()}
              </PrimaryButton>
            </Col>
          ) : null}
        </Row>
        <Row>
          <Col>
            <BasicTable
              columns={columns}
              data={configurations}
              page={tablePage}
              sizePerPage={pageSize}
              totalSize={totalSize}
              handleTableChange={handleTableChange}
              setSelectedRow={setSelectedRow}
              keyField="id"
            />
          </Col>
        </Row>
      </div>
      <DownloadExcelList configurations={configurations} />
    </Container>
  );
};

export default Project;
